import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, MapPin, Clock, Check, X, Thermometer, Wind, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function DestinationDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [weather, setWeather] = useState(null);
  const [isFav, setIsFav] = useState(false);
  const [travelers, setTravelers] = useState(2);
  const [inq, setInq] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [bookingName, setBookingName] = useState(user?.name || "");
  const [bookingPhone, setBookingPhone] = useState(user?.phone || "");
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    api.get(`/destinations/${slug}`).then(({ data }) => {
      setD(data);
      const q = data.city || data.country;
      if (q) api.get("/weather", { params: { city: q } }).then(({ data: w }) => setWeather(w)).catch(() => {});
    });
    if (user) api.get("/favorites").then(({ data }) => setIsFav(data.some((x) => x.slug === slug))).catch(() => {});
  }, [slug, user]);

  useEffect(() => {
    if (!d || d.slug !== slug) return;

    const location = [d.city, d.country].filter(Boolean).join(", ");
    const duration = d.duration_days
      ? `${d.duration_days} days${Number(d.duration_nights) > 0 ? ` / ${d.duration_nights} nights` : ""}`
      : "";
    const titleParts = [d.name, "Tour Package", "OITS Travel Together"];
    document.title = titleParts.join(" | ");

    const sourceDescription = (d.short_description || d.description || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const context = [location, duration].filter(Boolean).join(" · ");
    const lead = context ? `${d.name}${location ? ` in ${location}` : ""}${duration ? ` — ${duration}` : ""}.` : `${d.name} tour package.`;
    const fullDescription = [lead, sourceDescription, "Explore the itinerary with OITS Travel Together."]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ");
    const descriptionText = fullDescription.length > 160
      ? `${fullDescription.slice(0, 157).replace(/\s+\S*$/, "")}…`
      : fullDescription;

    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }
    description.setAttribute("content", descriptionText);

    const setMeta = (property, value, attribute = "property") => {
      let meta = document.querySelector(`meta[${attribute}="${property}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, property);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", value);
    };
    setMeta("og:title", document.title);
    setMeta("og:description", descriptionText);
    setMeta("og:type", "website");

    setCurrentImage(0);
  }, [d, slug]);

  const toggleFav = async () => {
    if (!user) { toast.error("Please log in to save favorites"); return; }
    if (isFav) { await api.delete(`/favorites/${d.id}`); setIsFav(false); toast.success("Removed from favorites"); }
    else { await api.post(`/favorites/${d.id}`); setIsFav(true); toast.success("Added to favorites"); }
  };

  const nav = useNavigate();

  const bookNow = async () => {
    if (!user) { toast.error("Please log in to book"); return; }
    const name = bookingName.trim();
    const phone = bookingPhone.replace(/\\D/g, "");
    if (name.length < 2) { toast.error("Please enter your full name"); return; }
    if (phone.length < 10) { toast.error("Please enter a valid 10-digit mobile number"); return; }
    setLoading(true);
    try {
      const { data: order } = await api.post("/payments/order", {
        destination_id: d.id,
        travelers,
        origin_url: window.location.origin,
        customer_name: name,
        customer_phone: phone,
      });

      if (!window.Cashfree) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const cashfree = window.Cashfree({ mode: "production" });
      const result = await cashfree.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: "_self",
      });

      if (result?.error) {
        try { await api.post("/payments/cancel", { order_id: order.order_id }); } catch (_) {}
        toast.error(result.error.message || "Payment could not be started");
        setLoading(false);
        nav("/payment/cancel");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment initialization failed");
      setLoading(false);
    }
  };

  const sendInquiry = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inquiries", { ...inq, destination_id: d.id, destination_name: d.name, travelers });
      toast.success("Inquiry sent — we'll be in touch!");
      setInq({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Failed to send inquiry"); }
  };

  if (!d) return <div className="max-w-7xl mx-auto px-6 py-24 text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="text-xs uppercase tracking-widest text-accent mb-2">{d.category?.replace("-", " ")}</div>
      <h1 className="heading-serif text-4xl sm:text-6xl mb-3">{d.name}</h1>
      <div className="flex items-center gap-5 text-sm text-muted-foreground mb-8">
        <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {d.city}{d.country ? `, ${d.country}` : ""}</span>
        <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {d.duration_days}D / {d.duration_nights}N</span>
      </div>
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="relative rounded-2xl overflow-hidden border border-border">
            <img src={d.gallery?.[currentImage] || d.image_url} alt={d.name} className="w-full h-[420px] object-cover" />
            <button onClick={toggleFav} data-testid="detail-fav-btn" className="absolute top-4 right-4 h-11 w-11 rounded-full bg-black/50 backdrop-blur flex items-center justify-center"><Heart className={`h-5 w-5 ${isFav ? "fill-accent text-accent" : "text-white"}`} /></button>
          </div>
          {d.gallery?.length > 1 && <div className="flex gap-2 mt-3 overflow-x-auto">{d.gallery.map((src, i) => <button key={src} type="button" onClick={() => setCurrentImage(i)} className={`shrink-0 rounded-lg overflow-hidden border-2 ${currentImage === i ? "border-accent" : "border-transparent"}`} aria-label={`View image ${i + 1}`}><img src={src} alt="" className="w-20 h-14 object-cover" /></button>)}</div>}
          <p className="mt-8 text-base leading-relaxed">{d.description}</p>
          {d.highlights?.length > 0 && <div className="mt-10"><h2 className="heading-serif text-3xl mb-4">Highlights</h2><ul className="grid sm:grid-cols-2 gap-3">{d.highlights.map((h, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-accent mt-0.5" /> {h}</li>)}</ul></div>}
          {d.itinerary?.length > 0 && <div className="mt-12"><h2 className="heading-serif text-3xl mb-2">Day-by-day itinerary</h2><Accordion type="single" collapsible defaultValue="day-1" data-testid="itinerary-accordion">{d.itinerary.map((day) => <AccordionItem key={day.day} value={`day-${day.day}`}><AccordionTrigger className="text-left"><span className="flex items-center gap-4"><span className="heading-serif text-2xl text-accent">Day {day.day}</span><span className="font-medium">{day.title}</span></span></AccordionTrigger><AccordionContent className="text-sm text-muted-foreground leading-relaxed">{day.description}</AccordionContent></AccordionItem>)}</Accordion></div>}
          <div className="grid sm:grid-cols-2 gap-6 mt-10">
            {d.inclusions?.length > 0 && <div><h3 className="heading-serif text-2xl mb-3">Inclusions</h3><ul className="space-y-2 text-sm">{d.inclusions.map((x, i) => <li key={i} className="flex items-start gap-2"><Check className="h-4 w-4 text-accent mt-0.5" /> {x}</li>)}</ul></div>}
            {d.exclusions?.length > 0 && <div><h3 className="heading-serif text-2xl mb-3">Exclusions</h3><ul className="space-y-2 text-sm">{d.exclusions.map((x, i) => <li key={i} className="flex items-start gap-2"><X className="h-4 w-4 text-destructive mt-0.5" /> {x}</li>)}</ul></div>}
          </div>
        </div>
        <aside className="lg:col-span-4"><div className="lg:sticky lg:top-32 space-y-6">
          <div className="border border-border rounded-xl p-6 bg-card">
            {Number(d.price) > 0 && <><div className="text-xs uppercase tracking-widest text-muted-foreground">Starting from</div><div className="text-4xl font-bold mt-1">₹{Number(d.price).toLocaleString("en-IN")}</div><div className="text-xs text-muted-foreground">per person · {d.duration_days} days</div></>}
            <div className="mt-6 flex items-center justify-between"><label className="text-sm">Travelers</label><div className="flex items-center gap-2"><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setTravelers(Math.max(1, travelers - 1))} data-testid="travelers-minus">−</Button><span className="w-6 text-center text-sm" data-testid="travelers-count">{travelers}</span><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setTravelers(travelers + 1)} data-testid="travelers-plus">+</Button></div></div>
            {Number(d.price) > 0 && <div className="mt-4 flex justify-between text-sm"><span className="text-muted-foreground">Total</span><span className="font-semibold">₹{(d.price * travelers).toLocaleString("en-IN")}</span></div>}
            <Input required placeholder="Full name for booking" value={bookingName} onChange={(e) => setBookingName(e.target.value)} className="mt-5" />
            <Input required inputMode="numeric" maxLength={15} placeholder="Mobile number for payment" value={bookingPhone} onChange={(e) => setBookingPhone(e.target.value)} className="mt-2" />
            <Button onClick={bookNow} disabled={loading} data-testid="book-now-btn" className="mt-5 w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">{loading ? "Opening secure checkout..." : "Book Now"}</Button>
            <Button onClick={toggleFav} variant="outline" data-testid="fav-toggle-btn" className="mt-2 w-full rounded-full"><Heart className={`mr-2 h-4 w-4 ${isFav ? "fill-accent text-accent" : ""}`} /> {isFav ? "Saved" : "Save to favorites"}</Button>
          </div>
          {weather && <div className="border border-border rounded-xl p-5 bg-card" data-testid="weather-widget"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Live weather at {d.city || d.country}</div><div className="flex items-baseline gap-2"><span className="heading-serif text-5xl">{weather.temperature_c}°</span><span className="text-sm text-muted-foreground">{weather.condition}</span></div><div className="mt-3 grid grid-cols-3 gap-2 text-xs text-muted-foreground"><div className="flex items-center gap-1"><Thermometer className="h-3 w-3" /> Feels {weather.feels_like_c}°</div><div className="flex items-center gap-1"><Droplets className="h-3 w-3" /> {weather.humidity}%</div><div className="flex items-center gap-1"><Wind className="h-3 w-3" /> {weather.wind_kmph} kmph</div></div></div>}
          <form onSubmit={sendInquiry} className="border border-border rounded-xl p-5 bg-card" data-testid="detail-inquiry-form"><div className="heading-serif text-2xl mb-3">Custom inquiry</div><Input required placeholder="Name" value={inq.name} onChange={(e) => setInq({ ...inq, name: e.target.value })} data-testid="detail-inq-name" /><Input required type="email" placeholder="Email" className="mt-2" value={inq.email} onChange={(e) => setInq({ ...inq, email: e.target.value })} data-testid="detail-inq-email" /><Input required type="tel" placeholder="Phone" className="mt-2" value={inq.phone} onChange={(e) => setInq({ ...inq, phone: e.target.value })} data-testid="detail-inq-phone" /><Textarea required rows={3} placeholder="Any customisation?" className="mt-2" value={inq.message} onChange={(e) => setInq({ ...inq, message: e.target.value })} data-testid="detail-inq-message" /><Button type="submit" data-testid="detail-inq-submit" className="mt-3 w-full rounded-full bg-primary text-primary-foreground">Send inquiry</Button></form>
        </div></aside>
      </div>
    </div>
  );
}
